import Vue from 'vue';
import Vuex from 'vuex';
import firebase from 'firebase/app';
import contributors from './modules/contributors';
import episodes from './modules/episodes';
import stories from './modules/stories';
import tags from './modules/tags';
import users from './modules/users';
import 'firebase/firestore';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {},
  getters: {},
  mutations: {
    setItem(state, { item, id, resource }) {
      Vue.set(state[resource].items, id, item);
    },
    setItems(state, { items, resource }) {
      Object.entries(items).forEach(entry => Vue.set(state[resource].items, entry[0], entry[1]));
    },
  },
  actions: {
    createItem({ commit }, { resource, item }) {
      return new Promise((resolve) => {
        firebase.firestore()
          .collection(resource)
          .add(item)
          .then((res) => {
            const { id } = res;
            commit('setItem', { resource, id, item });
            resolve({ [id]: item });
          })
          .catch(error => console.error(`Error adding ${resource} resource: `, error));
      });
    },
    updateItem({ commit }, { resource, id, data }) {
      const docRef = firebase.firestore().collection(resource).doc(id);
      // https://firebase.google.com/docs/firestore/manage-data/add-data?authuser=0#update-data
      return new Promise((resolve) => {
        docRef.update(data)
          .then(() => {
            docRef.get().then((response) => {
              commit('setItem', { resource, id, item: response.data() });
              resolve();
            });
          });
      });
    },
    fetchItem({ state, commit }, { id, resource }) {
      if (state[resource].items[id]) {
        return state[resource].items[id];
      }

      return new Promise((resolve) => {
        firebase.firestore().collection(resource).doc(id).get()
          .then((response) => {
            commit('setItem', { resource, id, item: response.data() });
            resolve(state[resource].items[id]);
          });
      });
    },
    fetchItems({ dispatch }, { ids, resource }) {
      ids = Array.isArray(ids) ? ids : Object.keys(ids); // eslint-disable-line
      return Promise.all(ids.map(id => dispatch('fetchItem', { id, resource })));
    },
    async findItem({ state, dispatch }, { resource, query }) {
      // find and return a specific item
      const { items } = state[resource];

      // search current state for queried item
      const id = Object.keys(items).find((k) => {
        const data = items[k];

        return Object.entries(query) // split object into elements from object key & value
          .map(e => data[e[0]] === e[1]) // remap array to boolean checking values
          .every(b => b); // return true if all queries were true
      });

      if (id) return { [id]: items[id] };

      // if not in store, query firebase
      return dispatch('findItems', { resource, query });
    },
    findItems({ commit }, {
      resource,
      query,
      order,
      limit
    }) {
      let collection = firebase.firestore().collection(resource);

      // const compare = (obj, args) => {
      //   const [ field, operator, value ] = args;
      //   const post = obj[field];
      //
      //   switch (operator) {
      //     case '>':   return post > value;
      //     case '<':   return post < value;
      //     case '>=':  return post >= value;
      //     case '<=':  return post <= value;
      //     case '==':  return post == value;
      //     case '!=':  return post != value;
      //     case '===': return post === value;
      //     case '!==': return post !== value;
      //   }
      // }

      const addQuery = (q) => {
        const [field, operator, value] = q;
        collection = collection.where(field, operator, value);
      };

      // if no query resume to get whole collection
      if (query) {
        // Assumes query is object if not array
        if (!Array.isArray(query)) {
          // eslint-disable-next-line
          query = Object.entries(query).map(e => [e[0], '==', e[1]]);
        }

        // Assumes multi-query if first element is array
        if (Array.isArray(query[0])) {
          query.forEach(arr => addQuery(arr));
        } else {
          // single query
          addQuery(query);
        }
      }

      if (order) {
        if (Array.isArray(order)) {
          Object.values(order).forEach((obj) => {
            collection = collection.orderBy(obj.field, obj.sort || 'asc');
          });
        } else {
          // single ordering
          collection = collection.orderBy('episode');
        }
      }

      if (limit) {
        collection = collection.limit(limit);
      }

      return new Promise((resolve) => {
        collection.get().then((querySnapshot) => {
          const items = {};

          querySnapshot.forEach((doc) => {
            // doc.data() is never undefined for query doc snapshots
            items[doc.id] = doc.data();
          });

          commit('setItems', { resource, items });

          resolve(items);
        });
      });
    }
  },
  modules: {
    contributors,
    episodes,
    stories,
    tags,
    users
  }
});
